// Message templates: WhatsApp templates (approved by Meta, sent to it through Exotel's template API, plan v2 §5.5)
// and SMS DLT templates (SPEC §3). Wording never changes: new words = a new template name with a version. A WhatsApp
// template's words carry {{1}} … {{5}} (CW2: each Send WhatsApp step picks what fills each one, the first name by
// default); the link goes on its button, which opens our own short link (LINK_BUTTON_URL), and that leads on to the
// lender's page or to Apply now.
import { z } from 'zod'
import { Id, IsoDate } from './common'

export const TemplateChannel = z.enum(['whatsapp', 'sms'])
export type TemplateChannel = z.infer<typeof TemplateChannel>

export const TemplateStatus = z.enum(['pending', 'approved', 'rejected'])
export type TemplateStatus = z.infer<typeof TemplateStatus>

/** WhatsApp: marketing / utility. SMS (DLT): promotional / transactional. */
export const TemplateCategory = z.enum(['marketing', 'utility', 'promotional', 'transactional'])
export type TemplateCategory = z.infer<typeof TemplateCategory>

export const TemplateLanguage = z.enum(['en', 'hi'])
export type TemplateLanguage = z.infer<typeof TemplateLanguage>

/** The button's address: one fixed site with the person's code at the end (Meta allows only that), never a lender's. */
export const LINK_BUTTON_URL = 'https://go.firstfundfincorp.com/l/{{1}}'

export const TemplateButton = z.object({
  text: z.string().describe('what the button says, e.g. Continue'),
  url: z.string().describe("Meta's address of the button (synced); its {{1}} is filled at send time"),
  index: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe("the link button's place among the template's buttons (synced; none = the first)"),
})
export type TemplateButton = z.infer<typeof TemplateButton>

/** A button whose address ends in {{1}} takes a value at send time; a fixed address takes none (Meta refuses one). */
export const buttonTakesValue = (button: TemplateButton | null | undefined) =>
  !!button && /\{\{\s*1\s*\}\}/.test(button.url)

/** PD61 / PD62 (owner 2026-10-29/30): what fills the button's {{1}}: the person's short link to Apply now (counts the
 *  click; utm_content = the lead code), the lead code itself, the end part of the lender's link, or a fixed text. */
export const ButtonValue = z.enum(['short_link', 'lead_code', 'lender_link', 'lender_ref', 'fixed_text'])
export type ButtonValue = z.infer<typeof ButtonValue>

export const BUTTON_VALUE_LABEL: Record<ButtonValue, string> = {
  short_link: 'Short link to Apply now (counts the click)',
  lead_code: 'Lead code (FF-…)',
  lender_link: "The lender's link from its answer (the part after Meta's address)",
  lender_ref: "The lender's reference from its answer (its lead id)",
  fixed_text: 'A fixed text',
}

/** PD62: the choice, or the default that follows Meta's address: our go. address → the short link; a lender's own
 *  address → the lead code (the owner's way: the lender's UTM link with the code at its end, nothing of ours between).
 *  The short link works only on our go. address: chosen on a lender's button (a draft's old default), the lead code
 *  goes there instead (owner 2026-11-02). */
export function buttonValueOf(
  cfg: { button_value?: unknown },
  button: TemplateButton | null | undefined
): ButtonValue {
  const parsed = ButtonValue.safeParse(cfg.button_value)
  const ours = button?.url === LINK_BUTTON_URL
  if (parsed.success && (parsed.data !== 'short_link' || ours)) return parsed.data
  return ours ? 'short_link' : 'lead_code'
}

/** Meta's address before the {{1}}: what every link of this button starts with. */
export const buttonBase = (button: TemplateButton) => button.url.replace(/[{][{] *1 *[}][}].*$/, '')

/** The end part of the lender's link that fits the button (the link minus Meta's address); null = it does not fit,
 *  so the button cannot carry it (a different site, or a different order of the UTM fields). */
export function lenderLinkEnd(button: TemplateButton, link: string): string | null {
  const base = buttonBase(button)
  return link.startsWith(base) ? link.slice(base.length) : null
}

export const Template = z.object({
  id: Id,
  channel: TemplateChannel,
  name: z.string(),
  lang: TemplateLanguage,
  category: TemplateCategory,
  status: TemplateStatus,
  body: z
    .string()
    .describe(
      "WhatsApp: {{1}} … {{5}}, filled at send time by the step's picks (never stored); SMS: {{2}} = the link"
    ),
  button: TemplateButton.nullable().describe('WhatsApp: the link button; null for SMS'),
  header_image: z
    .string()
    .nullable()
    .describe(
      "W1: our copy of the template's header image (Sync copies Meta's sample; Meta does not fetch that for a send); '' = not copied yet; null = no image header"
    ),
  vendor_id: Id,
  vendor_name: z.string(),
  waba_id: z
    .string()
    .nullable()
    .describe(
      'WT1: the WhatsApp account (WABA) it belongs to: Meta keeps templates per WABA and sends one only from that WABA\'s numbers; null for SMS'
    ),
  waba_name: z.string().nullable().describe("WT1: that account's name"),
  dlt_id: z.string().nullable().describe('SMS only: the DLT template id'),
  sender_id: z
    .string()
    .nullable()
    .describe('SMS only: the DLT sender id (header) it goes out from, e.g. FFINCP'),
  opt_out: z
    .boolean()
    .describe(
      'an opt-out in the words, the footer or a quick-reply button (STOP, e.g. "Stop promotions"); a marketing template without one gets a warning'
    ),
  for_campaigns: z
    .boolean()
    .describe("false = a service template (e.g. the app's acknowledgement): never offered to a campaign"),
  created: IsoDate,
})
export type Template = z.infer<typeof Template>

export const TemplateFilter = z.object({ channel: TemplateChannel.optional() })
export type TemplateFilter = z.infer<typeof TemplateFilter>

/** CW2: a WhatsApp template may carry {{1}} … {{5}}; each is filled at send time by what its step picks. */
export const MAX_TEMPLATE_VALUES = 5

/** What fills one {{n}}: a field of the person (from the registry at send time, never stored), the lead code
 *  (FF-…, as on Apply now), the campaign's name, the step's fixed text, or nothing (not_used: only right when the
 *  template has no such {{n}}). */
export const TemplateValue = z.enum([
  'first_name',
  'full_name',
  'city',
  'state',
  'pin_code',
  'loan_amount',
  'employment',
  'lead_code',
  'lender_ref',
  'campaign_name',
  'fixed_text',
  'not_used',
])
export type TemplateValue = z.infer<typeof TemplateValue>

export const TEMPLATE_VALUE_LABEL: Record<TemplateValue, string> = {
  first_name: 'First name',
  full_name: 'Full name',
  city: 'City',
  state: 'State',
  pin_code: 'PIN code',
  loan_amount: 'Loan amount (₹)',
  employment: 'Employment (salaried / self-employed)',
  lead_code: 'Lead code (FF-…)',
  lender_ref: "The lender's reference (its lead id, from the Lender step's answer)",
  campaign_name: "The campaign's name",
  fixed_text: 'The fixed text below',
  not_used: 'Not used',
}

/** The step settings that fill a template (the Send WhatsApp node's value_1 … value_5, fixed_text, fallback). */
export interface TemplateValues {
  value_1?: unknown
  value_2?: unknown
  value_3?: unknown
  value_4?: unknown
  value_5?: unknown
  fixed_text?: unknown
  fallback?: unknown
}

/** The {{n}} a body uses, each once, smallest first: "Hi {{1}}, {{2}} … {{1}}" → [1, 2]. */
export function templateNumbers(body: string): number[] {
  const found = [...body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]))
  return [...new Set(found)].sort((a, b) => a - b)
}

/** What a step picked for {{n}}; a step saved before CW2 has no value_1: the first name, as it always was. */
export function valueOf(cfg: TemplateValues, n: number): TemplateValue {
  const raw = cfg[`value_${n}` as keyof TemplateValues]
  const parsed = TemplateValue.safeParse(raw)
  if (parsed.success) return parsed.data
  return n === 1 ? 'first_name' : 'not_used'
}

/** Why a step cannot send this template yet, one line per {{n}} (empty = it can). */
export function valueProblems(body: string, cfg: TemplateValues): { n: number; message: string }[] {
  const problems: { n: number; message: string }[] = []
  for (const n of templateNumbers(body)) {
    if (n < 1 || n > MAX_TEMPLATE_VALUES) {
      problems.push({
        n,
        message: `The template uses {{${n}}}: at most {{1}} … {{${MAX_TEMPLATE_VALUES}}} can be filled`,
      })
      continue
    }
    const value = valueOf(cfg, n)
    if (value === 'not_used') problems.push({ n, message: `{{${n}}} has no value: pick what fills it` })
    if (value === 'fixed_text' && !String(cfg.fixed_text ?? '').trim())
      problems.push({ n, message: `{{${n}}} is the fixed text: type the text` })
  }
  return problems
}

/** The fields a template can be filled from (the person's, at send time; the campaign's name). */
export type TemplateFields = Partial<Record<Exclude<TemplateValue, 'fixed_text' | 'not_used'>, string | null>>

/** The words for each {{n}} of the body, in order ({{1}} first). An empty field gets the step's fallback word. */
export function fillValues(body: string, cfg: TemplateValues, fields: TemplateFields): string[] {
  const fallback = String(cfg.fallback ?? '').trim() || 'there'
  return templateNumbers(body).map((n) => {
    const value = valueOf(cfg, n)
    const text =
      value === 'fixed_text'
        ? String(cfg.fixed_text ?? '')
        : value === 'not_used'
          ? ''
          : (fields[value] ?? '')
    return text.trim() || fallback
  })
}

/** The body with its {{n}} filled (the preview, and what the person reads). */
export function fillBody(body: string, cfg: TemplateValues, fields: TemplateFields): string {
  const numbers = templateNumbers(body)
  const words = fillValues(body, cfg, fields)
  return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (all, n: string) => words[numbers.indexOf(Number(n))] ?? all)
}

/** Every Send WhatsApp step of a document whose template has a {{n}} nothing fills: what stops a publish or an
 *  approval (the same words on the mock and the API). A template not in the list is left to the other checks. */
export function whatsappStepProblems(
  document: { nodes: { type: string; name: string; cfg: Record<string, unknown> }[] },
  templates: { id: string; body: string }[]
): string[] {
  const problems: string[] = []
  for (const node of document.nodes.filter((n) => n.type === 'campaign.whatsapp')) {
    const template = templates.find((t) => t.id === node.cfg.template)
    if (template)
      for (const p of valueProblems(template.body, node.cfg)) problems.push(`"${node.name}": ${p.message}.`)
  }
  return problems
}

/** A loan amount as people read it: 200000 → ₹2,00,000. */
export function rupees(amount: number | null | undefined): string | null {
  return amount === null || amount === undefined ? null : '₹' + Math.round(amount).toLocaleString('en-IN')
}

export const CreateTemplateInput = z.object({
  name: z
    .string()
    .trim()
    .regex(/^[a-z0-9_]+$/, 'Small letters, digits and _ only, e.g. loan_link_v2'),
  lang: TemplateLanguage,
  category: z.enum(['marketing', 'utility']),
  body: z
    .string()
    .trim()
    .min(10, 'Write the message')
    .max(1024)
    .refine(
      (body) => templateNumbers(body).every((n) => n >= 1 && n <= MAX_TEMPLATE_VALUES),
      `At most {{1}} … {{${MAX_TEMPLATE_VALUES}}}: the link goes on the button`
    )
    .refine(
      (body) => templateNumbers(body).every((n, i) => n === i + 1),
      'Number them in order, from {{1}} (no gaps): Meta refuses a template that skips one'
    ),
  button_text: z.string().trim().min(2, 'Say what the button says').max(25, 'At most 25 letters'),
})
export type CreateTemplateInput = z.infer<typeof CreateTemplateInput>

export const TemplateSyncAnswer = z.object({
  added: z.number().int().min(0).describe("made in Meta's WhatsApp Manager: now in the portal"),
  unchanged: z.number().int().min(0),
  accounts: z.number().int().min(0).describe('WT1: WhatsApp accounts (WABA) read'),
  failed: z
    .array(z.string())
    .describe("WT1: the accounts whose list could not be read (the others' templates still came in)"),
})
export type TemplateSyncAnswer = z.infer<typeof TemplateSyncAnswer>

export interface TemplatesApi {
  listTemplates(filter?: TemplateFilter): Promise<Template[]>
  /** sends a WhatsApp template to Meta through Exotel's template API: it starts as pending */
  createTemplate(input: CreateTemplateInput): Promise<Template>
  /** reads Exotel's template list of every WhatsApp account (WT1): templates made in Meta's WhatsApp Manager come in,
   *  each with Meta's status and its account */
  syncTemplates(): Promise<TemplateSyncAnswer>
  /** false keeps a service template out of every campaign picker; true offers it again */
  setTemplateForCampaigns(id: string, forCampaigns: boolean): Promise<Template>
}
